import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useEffect, useState, ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CustomLegend from './CustomLegend';
import CustomTooltip from './CustomTooltipTrends';
import {
  PERCENTAGE_TABLE,
  MetricBooleanMap,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import { TrendAndDetailsMetrics } from './types';
import { NoData } from '../../../../Util/NoData';
import DateCompare from '../../../../Types/DateCompare';
import Toggle from '@amzn/meridian/toggle';
import {
  calculateChartDataMinMax,
  ChartData,
  chartLineColors,
  hasValidDataPoints,
  generateTickRange,
  transformTrendData,
  sortChartDataByTimestamp,
} from './helper';
import CustomDot from './CustomDot';
import { TrendCategory } from '@amzn/aet-galactus-kit';

interface TeamGraphViewProps {
  trendAndDetailsMetrics: TrendAndDetailsMetrics;
  dateSelection: string;
  metricCategory: string;
}

const TeamGraphView: React.FC<TeamGraphViewProps> = ({
  trendAndDetailsMetrics,
  metricCategory,
  dateSelection,
}: TeamGraphViewProps): React.ReactElement => {
  const intl = useIntl();
  const [chartTrends, setChartTrends] = useState<ChartData[]>();
  const [checked, setChecked] = useState(true);
  const [chartTrendsContainsData, setChartTrendsContainsData] = useState(false);
  const [dataMin, setDataMin] = useState(0);
  const [dataMax, setDataMax] = useState(100);
  const [dataMetricIsPercentage, setDataMetricIsPercentage] =
    useState<boolean>(false);
  const [dataKeys, setDataKeys] = useState<string[]>([]);

  useEffect(() => {
    if (
      !trendAndDetailsMetrics ||
      !trendAndDetailsMetrics.metricDatas ||
      !trendAndDetailsMetrics.metricDatas.trend
    )
      return;

    if (!trendAndDetailsMetrics.metricDatas.trend[0]) {
      setChartTrendsContainsData(false);
      return;
    }

    const localChartTrends = transformTrendData(
      trendAndDetailsMetrics.metricDatas.trend
    );

    setDataKeys(
      localChartTrends.length > 0
        ? Object.keys(localChartTrends[0]).filter(key => key !== 'timestamp')
        : []
    );
    setDataMetricIsPercentage(
      PERCENTAGE_TABLE[dataKeys[0] as keyof MetricBooleanMap]
    );
    const { dataMin: localDataMin, dataMax: localDataMax } =
      calculateChartDataMinMax(localChartTrends, dataMetricIsPercentage);
    setDataMin(localDataMin);
    setDataMax(localDataMax);
    setChartTrendsContainsData(hasValidDataPoints(trendAndDetailsMetrics));
    setChartTrends(sortChartDataByTimestamp(localChartTrends));
  }, [trendAndDetailsMetrics]);

  return (
    <Column width='100%' spacing='none' spacingInset='none none 500 none'>
      {!chartTrendsContainsData && <NoData />}
      {chartTrends && chartTrendsContainsData && (
        <>
          <Row spacingInset='300' data-cy='team-view-title'>
            {metricCategory === TrendCategory.COMPLIANCE ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_team_compliance_metrics'
                  defaultMessage='Team Compliance Metrics'
                />
              </Text>
            ) : metricCategory === TrendCategory.OTHER ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_team_other_metrics'
                  defaultMessage='Team Other Metrics'
                />
              </Text>
            ) : metricCategory === TrendCategory.EFFICIENCY ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_team_efficiency_metrics'
                  defaultMessage='Team Efficiency Metrics'
                />
              </Text>
            ) : metricCategory === TrendCategory.QUALITY ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_team_quality_metrics'
                  defaultMessage='Team Quality Metrics'
                />
              </Text>
            ) : (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_nothing_to_show'
                  defaultMessage='Nothing to show yet.'
                />
              </Text>
            )}
          </Row>
          <Row width='100%' widths='fill' spacingInset='300'>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {dateSelection === DateCompare.DAY ? (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_days'
                      defaultMessage="Showing aggregated data of the team's trends over the past 15 days."
                    />
                  </Text>
                ) : dateSelection === DateCompare.WEEK ? (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_weeks'
                      defaultMessage="Showing aggregated data of the team's trends over the past 8 weeks."
                    />
                  </Text>
                ) : (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_months'
                      defaultMessage="Showing aggregated data of the team's trends over the past 2 months."
                    />
                  </Text>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div>
                  <Text type='b300'>
                    <FormattedMessage
                      id='cs_perfportal_network_benchmark'
                      defaultMessage='Network Benchmark'
                    />{' '}
                  </Text>
                </div>
                <div>
                  <Toggle checked={checked} onChange={setChecked}>
                    {checked}
                  </Toggle>
                </div>
              </div>
            </div>
          </Row>
          <Column
            width='100%'
            alignmentHorizontal='end'
            spacing='300'
            spacingInset='300'
            data-cy='graph-container'
          >
            <ResponsiveContainer width='99%' height={384}>
              <LineChart
                data={chartTrends}
                margin={{ top: 5, right: 30, left: -25, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis
                  dataKey='timestamp'
                  interval={0}
                  dx={0}
                  dy={20}
                  angle={0}
                  tick={{ fontSize: 10 }}
                  padding={
                    dateSelection === DateCompare.MONTH
                      ? { left: 50, right: 50 }
                      : { left: 20, right: 20 }
                  }
                  height={50}
                  minTickGap={-200}
                >
                  {!chartTrendsContainsData && (
                    <Label
                      value={intl.formatMessage({
                        id: 'cs_perfportal_noDataToReport',
                        defaultMessage: 'No data to report',
                      })}
                      position='top'
                    />
                  )}
                </XAxis>
                <YAxis
                  type='number'
                  allowDecimals={false}
                  tick={{ fontSize: 10 }}
                  ticks={generateTickRange(dataMin, dataMax)}
                  tickFormatter={
                    dataMetricIsPercentage
                      ? value => `${value.toFixed(0)}%`
                      : value => `${value.toFixed(0)}`
                  }
                  domain={
                    dataMetricIsPercentage ? [1, 100] : [dataMin, dataMax]
                  }
                  allowDataOverflow
                  padding={{ top: 20, bottom: 20 }}
                />
                <Tooltip content={<CustomTooltip />} />
                {dataKeys.map((key, index) =>
                  checked || (!checked && !key.includes('#netavg')) ? (
                    <Line
                      name={key}
                      dataKey={key}
                      stroke={chartLineColors[index]}
                      strokeWidth={3}
                      type='monotone'
                      dot={
                        <CustomDot
                          chartTrends={chartTrends}
                          dataKey={key}
                          color={chartLineColors[index]}
                        />
                      }
                      strokeDasharray={key.includes('#netavg') ? '5 2' : 'none'}
                      isAnimationActive={false}
                    />
                  ) : null
                )}
                <Legend
                  verticalAlign='bottom'
                  align='left'
                  wrapperStyle={{ fontSize: '16px', marginLeft: '50px' }}
                  content={(props: any): ReactElement => (
                    <CustomLegend payload={props.payload} />
                  )}
                />
              </LineChart>
            </ResponsiveContainer>
          </Column>
        </>
      )}
    </Column>
  );
};

export default TeamGraphView;
